// The bee inspect panel (bee-inspect-panel; `client-colony` "Tapping a bee opens the inspect
// panel", UI.md §4/§7): the floating card that opens when the player taps a bee on the Colony
// canvas. Composed from the shared kit — `Panel` for the card, `ProgressBar` for the two
// condition meters, `Badge` for the own/enemy allegiance mark, `Button` for follow — in the
// Field Journal chrome treatment, layered over the canvas exactly like `CameraControl`.
//
// PRESENTATIONAL, like `CameraControl`: the owning screen holds the selection + follow state
// and passes a `BeeInspectModel` (the pure `bee-inspect-model.ts` mapping). This component
// reads no store and picks nothing.
//
// Two contracts worth naming:
//   - `data-gesture-chrome` on the root. The panel floats INSIDE the canvas gesture
//     container, so without this marker a tap on the panel body would bubble to the gesture
//     adapter and re-pick whatever bee the panel is covering.
//   - The `lost` model renders NO meters, action, target or follow control. The wire carries
//     no death signal (UI.md §8), so once a bee leaves the accepted frame every number we
//     held is stale — showing them frozen would be a lie, and following a bee we can no
//     longer see is meaningless.

import type { CSSProperties } from 'react';
import { Panel } from '../core/Panel';
import { Badge } from '../core/Chip';
import { Button } from '../core/Button';
import { ProgressBar } from '../core/ProgressBar';
import type { BeeInspectModel } from '../../colony/bee-inspect-model';

export interface BeeInspectPanelProps {
  model: BeeInspectModel;
  /** Whether follow mode is currently engaged for this bee. */
  following: boolean;
  onToggleFollow: () => void;
  onDismiss: () => void;
  /** Vertical clearance (px) from the screen bottom — the Colony mount lifts the panel
   * clear of its full-width bee-type dock and the camera rail's tier chip. */
  bottomOffset?: number;
}

const ROW_LABEL: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 800,
  fontSize: 9,
  textTransform: 'uppercase',
  letterSpacing: 'var(--tracking-caps)',
  color: 'var(--text-muted)',
};

const ROW_VALUE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--ink-1)',
  fontVariantNumeric: 'tabular-nums',
};

/** A labelled meter row: micro-caps label, tabular percentage, and the kit `ProgressBar`. */
function Meter({ testId, label, value, colour }: { testId: string; label: string; value: number; colour: string }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div data-testid={testId}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
        <span style={ROW_LABEL}>{label}</span>
        <span style={{ ...ROW_VALUE, fontSize: 12 }}>{pct}%</span>
      </div>
      <ProgressBar value={value} max={1} color={colour} height={6} />
    </div>
  );
}

export function BeeInspectPanel({
  model,
  following,
  onToggleFollow,
  onDismiss,
  bottomOffset = 14,
}: BeeInspectPanelProps) {
  return (
    <div
      data-testid="bee-inspect-panel"
      // Marks the whole card (not only its buttons) as chrome for the canvas gesture
      // adapter, so a press anywhere on it is the panel's, never the field's.
      data-gesture-chrome=""
      style={{ position: 'absolute', left: 14, bottom: bottomOffset, width: 260, maxWidth: 'calc(100% - 76px)' }}
    >
      <Panel style={{ padding: 'var(--sp-3)', boxShadow: 'var(--shadow-hud)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 5,
              flex: 'none',
              background: model.status === 'live' ? model.typeColour : 'var(--line-strong)',
            }}
          />
          <b
            data-testid="bee-inspect-type"
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 15,
              color: 'var(--ink-1)',
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {model.status === 'live' ? model.typeLabel : `Bee #${model.id}`}
          </b>
          <button
            type="button"
            data-testid="bee-inspect-dismiss"
            title="Close"
            onClick={onDismiss}
            style={{
              width: 44,
              height: 44,
              minHeight: 44,
              flex: 'none',
              border: 'none',
              background: 'transparent',
              color: 'var(--ink-2)',
              fontSize: 15,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {model.status === 'lost' ? (
          <div data-testid="bee-inspect-lost" style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45 }}>
            Lost contact. This bee died, left the view, or was dropped by the close-tier cap —
            the stream cannot tell these apart.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span data-testid="bee-inspect-colony">
                <Badge tone={model.isOwnColony ? 'accent' : 'danger'}>{model.colonyLabel}</Badge>
              </span>
              <span style={{ ...ROW_LABEL, marginLeft: 'auto' }}>#{model.id}</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
              <Meter
                testId="bee-inspect-health"
                label="Health"
                value={model.healthFraction}
                colour={model.healthFraction < 0.3 ? 'var(--danger)' : 'var(--success)'}
              />
              <Meter
                testId="bee-inspect-nectar"
                label="Nectar"
                value={model.nectarFraction}
                colour="var(--honey-500)"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
              <span style={ROW_LABEL}>Action</span>
              <span data-testid="bee-inspect-action" style={ROW_VALUE}>
                {model.actionLabel}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={ROW_LABEL}>Target</span>
              <span data-testid="bee-inspect-target" style={ROW_VALUE}>
                {model.targetLabel}
              </span>
            </div>
            {/* The close tier carries no per-bee target (PROTOCOL §4) — say so rather than
                leave a plausible-looking blank the player would read as "no target". */}
            {!model.isDancing && (
              <div
                data-testid="bee-inspect-target-note"
                style={{ marginTop: 4, fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.4 }}
              >
                The close tier reports a target only while a bee is dancing.
              </div>
            )}

            <Button
              data-testid="bee-inspect-follow"
              variant={following ? 'primary' : 'secondary'}
              onClick={onToggleFollow}
              style={{ width: '100%', marginTop: 10 }}
            >
              {following ? 'Following' : 'Follow'}
            </Button>
          </>
        )}
      </Panel>
    </div>
  );
}
